import React, { useState, useEffect, useRef } from "react";
import Moment from "react-moment";
import { useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

import employerSvc from "../../../services/employer/employerSvc";
import StatusBadge from "./StatusBadge";
import { SectionIcon } from "./SectionIcons";
import StatsBanner from "./StatsBanner";
import SectionNavigation from "./SectionNavigation";
import {
  combineAndDeduplicateData,
  organizeDataIntoSections,
  extractStatsData
} from "../../../utils/spdHelpers";

const SummaryPlanDescription = () => {
  const location = useLocation();
  const employerId = location.pathname ? location.pathname.split("/")[2] : "";
  const planId = location.pathname ? location.pathname.split("/")[3] : "";

  const [loading, setLoading] = useState(true);
  const [employerInfo, setEmployerInfo] = useState({
    id: "",
    name: "",
    state: "",
  });
  const [empLogo, setEmpLogo] = useState(null);
  const [planType, setPlanType] = useState("");

  // New state for redesigned component
  const [sections, setSections] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeSection, setActiveSection] = useState("");

  // Refs for IntersectionObserver
  const sectionRefs = useRef([]);
  const isScrollingToSection = useRef(false);

  // MUI breakpoints to detect mobile screens (sm and below)
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const getSPDDetails = async (employerId) => {
      try {
        setLoading(true);
        const { employerInfo, plans, planGroups, logo } =
          await employerSvc.getEmployerDetails({
            employerIdForURL: employerId,
            page: "formSearch",
          });

        const currPlan = plans.find(
          (p) => p.planUniqueId.split("_")[1] === planId.split("-")[0]
        );
        const planGroup = planGroups.find(
          (pg) => pg.sk.split("_")[2] === planId.split("-")[0]
        );

        const universalPlanData = planGroup ? planGroup.universalPlanTypeValues : [];
        const currentPlanData = currPlan ? currPlan.planData : [];

        setPlanType(currPlan ? currPlan.label : "");

        setEmployerInfo({
          id: employerId,
          name: employerInfo.employerName,
          state: employerInfo.state,
        });

        setEmpLogo(logo);

        // Process data for new design
        const combined = combineAndDeduplicateData(universalPlanData, currentPlanData);
        const organizedSections = organizeDataIntoSections(combined);
        const extractedStats = extractStatsData(combined);

        setSections(organizedSections);
        setStats(extractedStats);

        // Set first section as active by default
        if (organizedSections.length > 0) {
          setActiveSection(organizedSections[0].id);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    getSPDDetails(employerId);
  }, [employerId, planId]);

  // IntersectionObserver for section tracking
  useEffect(() => {
    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Completely ignore during programmatic scroll
        if (isScrollingToSection.current) return;

        // Find the most visible section among intersecting entries
        let bestEntry = null;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!bestEntry || entry.intersectionRatio > bestEntry.intersectionRatio) {
              bestEntry = entry;
            }
          }
        });

        if (bestEntry) {
          setActiveSection(bestEntry.target.dataset.sectionId);
        }
      },
      {
        threshold: [0.1, 0.3, 0.5], // Multiple thresholds for better tracking
        rootMargin: "-80px 0px -40% 0px",
      }
    );

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [sections]);

  // Helper function to check if a field is a date field
  const isDateField = (normalizedKey) => {
    return normalizedKey.endsWith('date') || normalizedKey.endsWith('effectivedate');
  };

  // Helper function to get status border color
  const getStatusBorderColor = (status) => {
    switch (status) {
      case 'available':
        return 'rgba(16, 185, 129, 0.3)';
      case 'unavailable':
        return 'rgba(239, 68, 68, 0.3)';
      case 'partial':
        return 'rgba(245, 158, 11, 0.3)';
      default:
        return 'rgba(38, 55, 70, 0.06)';
    }
  };

  // Handle section navigation click
  const handleSectionClick = (sectionId) => {
    // Immediately update UI
    setActiveSection(sectionId);

    // Lock out observer during scroll
    isScrollingToSection.current = true;

    const element = document.querySelector(`[data-section-id="${sectionId}"]`);
    if (element) {
      const yOffset = -80; // Offset for sticky nav
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }

    // Re-enable observer after scroll completes
    // Listen for scrollend if supported, otherwise use debounced scroll detection
    if ('onscrollend' in window) {
      window.addEventListener('scrollend', () => {
        isScrollingToSection.current = false;
      }, { once: true });
    } else {
      // Fallback: detect scroll stop via debounce
      let scrollTimer;
      const onScroll = () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          isScrollingToSection.current = false;
          window.removeEventListener('scroll', onScroll);
        }, 150);
      };
      window.addEventListener('scroll', onScroll);
    }
  };

  if (loading) {
    return (
      <Container
        component="main"
        maxWidth="md"
        sx={{ py: 3, height: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <CircularProgress size={68} />
      </Container>
    );
  }

  // CSS keyframes for animations
  const fadeUpAnimation = {
    '@keyframes fadeUp': {
      '0%': {
        opacity: 0,
        transform: 'translateY(20px)',
      },
      '100%': {
        opacity: 1,
        transform: 'translateY(0)',
      },
    },
  };

  return (
    <Container component="main" maxWidth="lg" sx={{ py: 0, ...fadeUpAnimation }}>
      {/* Header with gradient background */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #263746 0%, #2e4456 40%, #344e62 100%)',
          borderBottom: '3px solid #0978d6',
          padding: isMobile ? '24px 16px' : '40px',
          marginBottom: '32px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Geometric overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: '40%',
            background: 'rgba(9, 120, 214, 0.12)',
            clipPath: 'polygon(20% 0, 100% 0, 100% 100%, 0% 100%)',
            pointerEvents: 'none',
          }}
        />

        {/* Header Content */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'auto 1fr auto',
            gap: isMobile ? '16px' : '32px',
            alignItems: 'center',
          }}
        >
          {/* TCG Logo */}
          <Box
            component="img"
            src="https://tcg-public-files.s3.us-east-1.amazonaws.com/HUB-TCG-Logo-white-transparent.png"
            alt="TCG Logo"
            sx={{
              width: isMobile ? '120px' : '160px',
              justifySelf: isMobile ? 'center' : 'start',
            }}
          />

          {/* Employer Info */}
          <Box
            sx={{
              textAlign: isMobile ? 'center' : 'center',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Fraunces, serif',
                fontSize: isMobile ? '24px' : '28px',
                fontWeight: 700,
                color: '#ffffff',
                marginBottom: '8px',
                lineHeight: 1.2,
              }}
            >
              {employerInfo.name}
            </Typography>
            {planType && (
              <Typography
                sx={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#5aade6',
                  marginBottom: '4px',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}
              >
                {planType}
              </Typography>
            )}
            <Typography
              sx={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: '16px',
                fontWeight: 400,
                color: 'rgba(255, 255, 255, 0.8)',
              }}
            >
              Summary Plan Description
            </Typography>
          </Box>

          {/* Employer Logo or Placeholder */}
          <Box
            sx={{
              width: isMobile ? '120px' : '160px',
              height: isMobile ? '80px' : '100px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              justifySelf: isMobile ? 'center' : 'end',
            }}
          >
            {empLogo && empLogo !== "N/A" ? (
              <Box
                component="img"
                src={empLogo}
                alt="Employer Logo"
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Main Content Container */}
      <Box sx={{ paddingX: isMobile ? '16px' : '0' }}>
        {/* Stats Banner */}
        <StatsBanner stats={stats} />

        {/* Section Navigation */}
        <SectionNavigation
          sections={sections}
          activeSection={activeSection}
          onSectionClick={handleSectionClick}
          isMobile={isMobile}
        />

        {/* Sections */}
        {sections.length > 0 ? (
          sections.map((section, index) => (
            <Box
              key={section.id}
              ref={(el) => (sectionRefs.current[index] = el)}
              data-section-id={section.id}
              sx={{
                marginBottom: '40px',
                animation: 'fadeUp 0.6s ease forwards',
                animationDelay: `${index * 80}ms`,
                opacity: 0,
                animationTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
              }}
            >
              {/* Section Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '16px',
                }}
              >
                {/* Icon Badge */}
                <Box
                  sx={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #0978d6 0%, #0968c4 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '12px',
                    color: '#ffffff',
                  }}
                >
                  <SectionIcon icon={section.icon} size={20} />
                </Box>

                {/* Section Title */}
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: 'Fraunces, serif',
                    fontSize: isMobile ? '20px' : '24px',
                    fontWeight: 600,
                    color: '#0f172a',
                  }}
                >
                  {section.title}
                </Typography>
              </Box>

              {/* Section Items */}
              <Paper
                sx={{
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                }}
              >
                {section.items.map((item, itemIndex) => (
                  <Box
                    key={item.normalizedKey}
                    sx={{
                      borderLeft: `3px solid ${getStatusBorderColor(item.status)}`,
                      padding: '16px',
                      backgroundColor: '#ffffff',
                      borderBottom:
                        itemIndex < section.items.length - 1
                          ? '1px solid #f1f5f9'
                          : 'none',
                      transition: 'background-color 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#f5f7f9',
                      },
                    }}
                  >
                    {/* Field Name */}
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'DM Sans, sans-serif',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#334155',
                        marginBottom: '6px',
                      }}
                    >
                      {item.fieldNameReadable}
                    </Typography>

                    {/* Field Value */}
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'DM Sans, sans-serif',
                        fontSize: '14px',
                        fontWeight: 400,
                        color: '#475569',
                        lineHeight: 1.6,
                      }}
                    >
                      {isDateField(item.normalizedKey) ? (
                        <Moment format="MM-DD-YYYY">{item.value}</Moment>
                      ) : (
                        item.value
                      )}
                    </Typography>

                    {/* Status Badge */}
                    {item.status && <StatusBadge status={item.status} />}
                  </Box>
                ))}
              </Paper>
            </Box>
          ))
        ) : (
          <Box
            sx={{
              textAlign: 'center',
              padding: '40px',
              color: '#64748b',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: '16px',
              }}
            >
              No plan information available.
            </Typography>
          </Box>
        )}

        {/* Contact Information */}
        <Paper
          sx={{
            backgroundColor: 'rgba(9, 120, 214, 0.08)',
            border: '1px solid rgba(9, 120, 214, 0.15)',
            borderRadius: '12px',
            padding: isMobile ? '20px 16px' : '24px 32px',
            textAlign: 'center',
            marginTop: '40px',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'DM Sans, sans-serif',
              fontSize: '15px',
              fontWeight: 400,
              color: '#0f172a',
              lineHeight: 1.6,
            }}
          >
            For more information please contact TCG Administrators, The Plan Administrator, at{" "}
            <Box
              component="span"
              sx={{
                fontWeight: 700,
                color: '#0978d6',
              }}
            >
              800-943-9179
            </Box>
          </Typography>
        </Paper>

        {/* Disclaimer */}
        <Box sx={{ marginTop: '24px', marginBottom: '40px' }}>
          <Typography
            sx={{
              fontFamily: 'DM Sans, sans-serif',
              fontSize: '12px',
              textAlign: 'center',
              color: '#94a3b8',
              lineHeight: 1.6,
            }}
          >
            This document is designed to inform Participants about the Plan in
            non-technical language. Every attempt is made to convey the Plan
            accurately. If anything in this Summary Plan Description varies from
            the Plan Documents, Plan Documents govern.
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default SummaryPlanDescription;
